// Lifted and adapted from Mantine V9.1.1

import { createStyles } from '@mantine/core';

const useStyles = createStyles(
  (
    theme,
    {
      backgroundColor,
      controlVariant,
      withFade,
      fillWidth,
    }: {
      backgroundColor: string;
      controlVariant: 'plain' | 'floating';
      withFade: boolean;
      fillWidth: boolean;
    },
    getRef
  ) => {
    const chipRef = getRef('chevronChip');
    const floatingOffset = controlVariant === 'floating' ? theme.other.spacing._16 : 0;
    const fade = (direction: 'right' | 'left') => {
      if (!withFade) return 'none';
      return controlVariant === 'floating'
        ? `linear-gradient(to ${direction}, ${backgroundColor} 0%, transparent 70%)`
        : `linear-gradient(to ${direction}, ${backgroundColor} 40%, transparent)`;
    };

    return {
      root: {
        '--scroller-control-size': '50px',
        '--scroller-control-offset': `${floatingOffset}px`,
        '--scroller-background-color': backgroundColor,

        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: controlVariant === 'floating' ? 'visible' : 'hidden',
        maxWidth: '100%',

        [`&:hover .${chipRef}, &:focus-within .${chipRef}`]: {
          opacity: 1,
          pointerEvents: 'auto',
        },

        // Offset by 8px to account for the exp. pill on the promoCard
        marginTop: -theme.other.spacing._8,

        [theme.fn.smallerThan('md')]: {
          '--scroller-control-offset': '0px',
        },
      },

      container: {
        paddingTop: theme.other.spacing._8,
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        userSelect: 'none',

        '&::-webkit-scrollbar': {
          display: 'none',
        },

        '&[data-draggable]': {
          cursor: 'grab',
        },
      },

      content: {
        display: 'inline-flex',
        whiteSpace: 'nowrap',
        gap: theme.other.spacing._8,
        ...(fillWidth && { minWidth: '100%' }),
      },

      chevron: {
        marginTop: theme.other.spacing._4,
        width: theme.other.spacing._16,
        height: theme.other.spacing._16,
      },

      control: {
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 'calc(var(--scroller-control-size) + var(--scroller-control-offset))',
        zIndex: 5,
        color: 'var(--mantine-color-dimmed)',
        opacity: 1,
        pointerEvents: 'auto',
        transition: 'opacity 200ms ease, color 150ms ease',

        '&:hover': {
          color: 'var(--mantine-color-text)',
        },

        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: 'var(--scroller-control-size)',
          pointerEvents: 'none',
        },

        '&:where([data-position="start"])': {
          insetInlineStart: 'calc(var(--scroller-control-offset) * -1)',
          justifyContent: 'flex-start',

          '&::before': {
            insetInlineStart: 'var(--scroller-control-offset)',
            background: fade('right'),
          },
        },

        '&:where([data-position="end"])': {
          insetInlineEnd: 'calc(var(--scroller-control-offset) * -1)',
          justifyContent: 'flex-end',

          '&::before': {
            insetInlineEnd: 'var(--scroller-control-offset)',
            background: fade('left'),
          },
        },

        '&:where([data-hidden])': {
          opacity: 0,
          pointerEvents: 'none',
        },
      },

      chevronChip: {
        ref: chipRef,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: theme.radius.xl,
        background: theme.white,
        boxShadow: theme.shadows.md,
        color: theme.colors.gray2[9],
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 150ms ease',

        '& svg': {
          marginTop: 0,
        },
      },
    };
  }
);

export default useStyles;
